import { useEffect, useRef } from 'react';

import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link, useSearchParams } from 'react-router';

import ImageCoverPage from 'components/ImageCoverPage';

import { useEmailVerification } from 'hooks/useEmailVerification';
import useSchoolContext from 'hooks/useSchoolContext';

import { VerificationStatusAlert } from './VerificationStatusAlert';

export function VerifyEmailCover() {
  const [searchParams] = useSearchParams();
  const schoolContext = useSchoolContext();
  const hasSentInitialRequest = useRef(false);

  // get token from query parameter
  const token = searchParams.get('token');

  // email verification logic (hook manages state, component controls when to verify)
  const { loading, success, error, verify, resend } = useEmailVerification(token);

  // Verify email on mount (prevent double-call in StrictMode)
  useEffect(() => {
    if (hasSentInitialRequest.current) return;
    verify();
    hasSentInitialRequest.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ImageCoverPage imgSrc={schoolContext?.loginImage}>
      <Stack gap={2}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            marginBottom: 2,
          }}
        >
          Verify Email
        </Typography>

        <VerificationStatusAlert
          loading={loading}
          success={success}
          error={error}
          onResend={resend}
          onRetry={() => token && verify()}
        />

        {/* Loading indicator for all operations */}
        <Collapse in={loading}>
          <LinearProgress />
        </Collapse>

        {/* only show the login button if the verified email is able to login (not a parent email) */}
        <Collapse in={success}>
          <Button component={Link} to="/login" size="large" variant="contained" fullWidth>
            Login
          </Button>
        </Collapse>
      </Stack>
    </ImageCoverPage>
  );
}

export default VerifyEmailCover;
